import type {
    ProductDependenciesConfig,
    ProductDependencyRule,
} from "../../../types/productDependencies";
import type { LineEdit } from "../types";
import type { MaterialRowItem } from "./materialLineItems";
import {
    buildCategoryEditKeys,
    resolveProductIdInOptions,
} from "./buildCategoryEditKeys";

type ApplyProductDependenciesParams = {
    editKey: string;
    category: string;
    selectedProductId: string | null;
    dependencies?: ProductDependenciesConfig;
    materialsByGroup: Record<string, MaterialRowItem[]>;
};

function matchesManufacturerProductId(
    selectedProductId: string,
    manufacturerProductId: string,
): boolean {
    return (
        selectedProductId === manufacturerProductId ||
        selectedProductId.startsWith(`${manufacturerProductId}-`)
    );
}

function collectManagedCategories(rule: ProductDependencyRule): Set<string> {
    const managedCategories = new Set<string>();
    for (const mapping of rule.mappings) {
        for (const req of mapping.requiredProducts) {
            managedCategories.add(req.category);
        }
    }
    return managedCategories;
}

export function applyProductDependencies({
    editKey,
    category,
    selectedProductId,
    dependencies,
    materialsByGroup,
}: ApplyProductDependenciesParams): Record<string, Partial<LineEdit>> {
    const patches: Record<string, Partial<LineEdit>> = {
        [editKey]: { selectedProductId },
    };

    if (!dependencies) return patches;

    const rule = dependencies.rules.find((r) => r.category === category);
    if (!rule) return patches;

    const managedCategories = collectManagedCategories(rule);
    if (managedCategories.size === 0) return patches;

    const { editKeyByCategory, optionsByEditKey } =
        buildCategoryEditKeys(materialsByGroup);

    const mapping = selectedProductId
        ? rule.mappings.find((m) =>
              matchesManufacturerProductId(
                  selectedProductId,
                  m.parentManufacturerProductId,
              ),
          )
        : null;

    const requiredByCategory = new Map<string, string>();
    if (mapping) {
        for (const req of mapping.requiredProducts) {
            requiredByCategory.set(req.category, req.manufacturerProductId);
        }
    }

    for (const cat of Array.from(managedCategories)) {
        const targetEditKey = editKeyByCategory[cat];
        if (!targetEditKey) continue;

        const requiredMfgId = requiredByCategory.get(cat);
        if (!requiredMfgId) {
            patches[targetEditKey] = { selectedProductId: null };
            continue;
        }

        const options = optionsByEditKey[targetEditKey] ?? [];
        const resolvedId = resolveProductIdInOptions(requiredMfgId, options);
        if (resolvedId) {
            patches[targetEditKey] = { selectedProductId: resolvedId };
        }
    }

    return patches;
}
